import { AddressLink } from '../common/AddressLink';
import OrderDetailsModal from './OrderDetailsModal';
import axios from 'axios';
import React, { useState, useEffect, useRef } from 'react';
import { apiClient, showToast } from '../../utils/apiClient';
import { API } from '../../config';
import { formatDate, formatDateTime, formatNotesWithBullets, getDayOfWeek, formatDateWithDay, getLocationDisplay, getLocationChipColor, getRowAlert, formatDatetimeForInput, parseUTCDatetime, getTodayInAEST, isPrepaymentApproaching, hasAppointments, getAppointmentSummary } from '../../utils/helpers';


const OrderHistoryModal = ({ onClose, showToast, onSwitchTab }) => {
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [activeTab, setActiveTab] = useState('orders'); // 'orders' or 'customers'

  // Fetch orders and customers from backend
  const fetchOrderHistory = async () => {
    try {
      setLoading(true);
      const [ordersResponse, customersResponse] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/online/orders`),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/online/customers`)
      ]);

      if (ordersResponse.ok && customersResponse.ok) {
        const ordersData = await ordersResponse.json();
        const customersData = await customersResponse.json();
        
        setOrders(ordersData || []);
        setCustomers(customersData || []);
      } else {
        console.error('Failed to fetch order history');
      }
    } catch (error) {
      console.error('Error fetching order history:', error);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchOrderHistory();
  }, []);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'Pending': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'Processing': 'bg-blue-100 text-blue-800 border-blue-200',
      'Shipped': 'bg-purple-100 text-purple-800 border-purple-200',
      'Delivered': 'bg-green-100 text-green-800 border-green-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-6xl mx-4 max-h-screen flex flex-col md:max-h-screen">
        {/* Header */}
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900">🛒 Online Sales</h2>
            <p className="text-sm md:text-base text-gray-600">Manage orders and create new sales</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl md:text-3xl font-bold"
          >
            ×
          </button>
        </div>

        {/* Top Tab Navigation */}
        <div className="flex border-b border-gray-200 px-4 md:px-6">
          <button
            onClick={() => onSwitchTab && onSwitchTab('orders')}
            className="px-4 py-3 text-sm font-semibold text-purple-700 border-b-2 border-purple-600"
            data-testid="sales-tab-orders"
          >
            📦 Order History
          </button>
          <button
            onClick={() => onSwitchTab && onSwitchTab('new_order')}
            className="px-4 py-3 text-sm font-medium text-gray-500 hover:text-gray-700 border-b-2 border-transparent"
            data-testid="sales-tab-new-order"
          >
            + New Order
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 p-3 md:p-6 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-32 md:h-64">
              <div className="animate-spin rounded-full h-8 w-8 md:h-12 md:w-12 border-b-2 border-indigo-600"></div>
              <span className="ml-4 text-gray-600 text-sm md:text-base">Loading orders...</span>
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-8 md:py-12">
              <div className="text-gray-400 mb-4">
                <svg className="h-12 w-12 md:h-16 md:w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2M4 13h2m13-8l-4 4m0 0l-4-4m4 4V3" />
                </svg>
              </div>
              <h3 className="text-base md:text-lg font-medium text-gray-900 mb-2">No online orders yet</h3>
              <p className="text-sm md:text-base text-gray-500">Orders from the online sales system will appear here.</p>
            </div>
          ) : (
            <div className="space-y-4 md:space-y-6">
              {/* Tab Navigation */}
              <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
                <button
                  onClick={() => setActiveTab('orders')}
                  className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
                    activeTab === 'orders'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  📦 Orders ({orders.length})
                </button>
                <button
                  onClick={() => setActiveTab('customers')}
                  className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
                    activeTab === 'customers'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  👥 Customers ({customers.length})
                </button>
              </div>

              {activeTab === 'orders' ? (
                <>
                  {/* Summary Stats - Mobile Optimized */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4 mb-4 md:mb-6">
                <div className="bg-blue-50 p-3 md:p-4 rounded-lg">
                  <h4 className="text-xs md:text-sm font-medium text-blue-700">Total Orders</h4>
                  <p className="text-lg md:text-2xl font-bold text-blue-900">{orders.length}</p>
                </div>
                <div className="bg-green-50 p-3 md:p-4 rounded-lg">
                  <h4 className="text-xs md:text-sm font-medium text-green-700">Total Revenue</h4>
                  <p className="text-lg md:text-2xl font-bold text-green-900">
                    ${orders.reduce((sum, order) => sum + (order.total_amount || 0), 0).toFixed(2)}
                  </p>
                </div>
                <div className="bg-purple-50 p-3 md:p-4 rounded-lg">
                  <h4 className="text-xs md:text-sm font-medium text-purple-700">Customers</h4>
                  <p className="text-lg md:text-2xl font-bold text-purple-900">{customers.length}</p>
                </div>
                <div className="bg-yellow-50 p-3 md:p-4 rounded-lg">
                  <h4 className="text-xs md:text-sm font-medium text-yellow-700">Avg Order</h4>
                  <p className="text-lg md:text-2xl font-bold text-yellow-900">
                    ${orders.length > 0 ? (orders.reduce((sum, order) => sum + (order.total_amount || 0), 0) / orders.length).toFixed(2) : '0.00'}
                  </p>
                </div>
              </div>

              {/* Mobile: Card View, Desktop: Table View */}
              <div className="lg:hidden">
                {/* Mobile Card View */}
                <div className="space-y-3">
                  {orders.map((order) => (
                    <div key={order.id} className="bg-white border rounded-lg p-4 shadow-sm">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <div className="text-sm font-mono font-medium text-indigo-600">
                            #{order.order_number}
                          </div>
                          <div className="text-xs text-gray-500">
                            {formatDate(order.order_date)}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-semibold text-gray-900">
                            ${(order.total_amount || 0).toFixed(2)}
                          </div>
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(order.order_status)}`}>
                            {order.order_status}
                          </span>
                        </div>
                      </div>
                      <div className="mb-2">
                        <div className="text-sm font-medium text-gray-900">
                          {order.customer_name}
                        </div>
                        <div className="text-xs text-gray-500">
                          {order.customer_email}
                        </div>
                      </div>
                      <div className="flex justify-between items-center">
                        <div className="text-xs text-gray-600">
                          {order.items?.length || 0} items
                          {order.pet_personalization && (
                            <span className="text-indigo-600 ml-1">🐾 {order.pet_personalization}</span>
                          )}
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => setSelectedOrder(order)}
                            className="text-xs text-indigo-600 hover:text-indigo-900 px-2 py-1 bg-indigo-50 rounded"
                          >
                            Details
                          </button>
                          <button
                            onClick={async () => {
                              if (window.confirm(`Delete order ${order.order_number}?\n\nThis action cannot be undone.`)) {
                                try {
                                  const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/online/orders/${order.id}`, {
                                    method: 'DELETE'
                                  });

                                  if (response.ok) {
                                    // Remove from state immediately for better UX
                                    setOrders(prev => prev.filter(o => o.id !== order.id));
                                    showToast(`✅ Order ${order.order_number} deleted successfully`, 'success');
                                  } else {
                                    const error = await response.json();
                                    showToast(`❌ Failed to delete order: ${error.detail || 'Unknown error'}`, 'error');
                                  }
                                } catch (error) {
                                  console.error('Delete order error:', error);
                                  showToast('❌ Failed to delete order. Please try again.', 'error');
                                }
                              }
                            }}
                            className="text-xs text-red-600 hover:text-red-900 px-2 py-1 bg-red-50 rounded"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Desktop: Table View */}
              <div className="hidden md:block bg-white rounded-lg border">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-medium">Recent Orders</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Order
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Customer
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Items
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Total
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {orders.map((order) => (
                        <tr key={order.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-mono font-medium text-indigo-600">
                              {order.order_number}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {order.customer_name}
                              </div>
                              <div className="text-sm text-gray-500">
                                {order.customer_email}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {order.items?.length || 0} items
                            </div>
                            {order.pet_personalization && (
                              <div className="text-xs text-indigo-600">
                                🐾 {order.pet_personalization}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-semibold text-gray-900">
                              ${(order.total_amount || 0).toFixed(2)}
                            </div>
                            <div className="text-xs text-gray-500">
                              inc. ${(order.shipping_cost || 0).toFixed(2)} shipping
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(order.order_status)}`}>
                              {order.order_status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(order.order_date)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => setSelectedOrder(order)}
                                className="text-indigo-600 hover:text-indigo-900"
                              >
                                View Details
                              </button>
                              <button
                                onClick={async () => {
                                  if (window.confirm(`Delete order ${order.order_number}?\n\nThis action cannot be undone.`)) {
                                    try {
                                      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/online/orders/${order.id}`, {
                                        method: 'DELETE'
                                      });

                                      if (response.ok) {
                                        showToast(`✅ Order ${order.order_number} deleted successfully`, 'success');
                                        fetchOrderHistory(); // Refresh the order list
                                      } else {
                                        const error = await response.json();
                                        showToast(`❌ Failed to delete order: ${error.detail || 'Unknown error'}`, 'error');
                                      }
                                    } catch (error) {
                                      console.error('Delete order error:', error);
                                      showToast('❌ Failed to delete order. Please try again.', 'error');
                                    }
                                  }
                                }}
                                className="text-red-600 hover:text-red-900"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
                </>
              ) : (
                <div className="space-y-4 md:space-y-6">
                  {/* Customers Tab Content */}
                  <div className="bg-white rounded-lg border">
                    <div className="px-6 py-4 border-b border-gray-200">
                      <h3 className="text-lg font-medium">Online Customers</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Customer
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Contact
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Address
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Orders
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {customers.map((customer) => (
                            <tr key={customer.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">
                                  {customer.customer_name}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">{customer.email}</div>
                                <div className="text-sm text-gray-500">{customer.phone}</div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="text-sm max-w-64"><AddressLink address={customer.shipping_address} postcode={customer.shipping_postcode} testId={`customer-address-link-${customer.id}`} /></div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">
                                  {orders.filter(order => order.customer_id === customer.id).length} orders
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                <button
                                  onClick={async () => {
                                    const customerOrders = orders.filter(order => order.customer_id === customer.id);
                                    if (customerOrders.length > 0) {
                                      alert(`Cannot delete customer. Customer has ${customerOrders.length} order(s). Delete orders first.`);
                                      return;
                                    }
                                    
                                    if (window.confirm(`Delete customer ${customer.customer_name}?\n\nThis action cannot be undone.`)) {
                                      try {
                                        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/online/customers/${customer.id}`, {
                                          method: 'DELETE'
                                        });

                                        if (response.ok) {
                                          // Remove from state immediately for better UX
                                          setCustomers(prev => prev.filter(c => c.id !== customer.id));
                                          showToast(`✅ Customer ${customer.customer_name} deleted successfully`, 'success');
                                        } else {
                                          const error = await response.json();
                                          showToast(`❌ Failed to delete customer: ${error.detail || 'Unknown error'}`, 'error');
                                        }
                                      } catch (error) {
                                        console.error('Delete customer error:', error);
                                        showToast('❌ Failed to delete customer. Please try again.', 'error');
                                      }
                                    }
                                  }}
                                  className="text-red-600 hover:text-red-900"
                                >
                                  Delete Customer
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Order Details Modal */}
      {selectedOrder && (
        <OrderDetailsModal
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
        />
      )}
    </div>
  );
};

// Order Details Modal Component

export default OrderHistoryModal;
